// contexts/AuthContext.js - Autentisering för KursAdmin (httpOnly cookie)
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL, COURSE_API_URL } from '../lib/api-config';

const AuthContext = createContext(null);

const AUTH_API_URL = `${CONTACT_API_URL}/auth`;
const COURSE_SYNC_URL = `${COURSE_API_URL}/auth/sync`;

// azurewebsites.net finns på Public Suffix List, så contact-api:s cookie
// skickas inte till course-api. Därför postar vi JWT:n till course-api:s
// sync-endpoint som sätter en egen httpOnly-cookie på sitt domän.
async function syncCourseApiCookie(token) {
  if (!token) return;
  try {
    await axios.post(COURSE_SYNC_URL, null, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    });
  } catch (err) {
    console.error('Course API cookie-sync misslyckades:', err?.response?.status);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validera session vid start (cookie skickas automatiskt)
  useEffect(() => {
    async function validateSession() {
      try {
        const response = await axios.get(`${AUTH_API_URL}/me`, {
          withCredentials: true
        });
        setUser(response.data.data);
      } catch (error) {
        // Ingen giltig session - användaren är ej inloggad
        setUser(null);
      }
      setLoading(false);
    }
    validateSession();
  }, []);

  // Logga in
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${AUTH_API_URL}/login`, {
        email,
        password
      }, { withCredentials: true });

      if (response.data.success) {
        // Synka course-api-cookien. Token:en finns bara i minnet denna stund.
        await syncCourseApiCookie(response.data.data.token);
        setUser(response.data.data.user);
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Inloggning misslyckades'
      };
    }
  };

  // Logga ut
  const logout = async () => {
    try {
      await axios.post(`${AUTH_API_URL}/logout`, {}, { withCredentials: true });
    } catch (error) {
      // Ignorera fel vid logout
    }
    setUser(null);
    // Rensa eventuell kvarvarande localStorage från före migrering
    localStorage.removeItem('kursadmin_token');
  };

  // Kontrollera om användaren har en specifik roll
  const hasRole = (role) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth måste användas inom en AuthProvider');
  }
  return context;
}
